import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  TableSortLabel,
  Collapse,
  Tooltip,
  alpha,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import UndoIcon from '@mui/icons-material/Undo';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { TextDiff, Flex } from '../';
import {
  THEME_COLORS,
  STATUS_TYPES,
  SORT_FIELDS,
  SIMILARITY_THRESHOLDS,
  getSectionNames,
  getEditType,
  getScoreLabel,
} from './AIEditsTab.utils';

const AIEditsTabTable = ({
  filteredAndSortedSentences,
  sortBy,
  sortOrder,
  expandedRows,
  processingIds,
  onSort,
  onToggleExpand,
  onApprove,
  onDisapprove,
  onUndo,
}) => {
  return (
    <TableContainer
      component={Paper}
      elevation={0}
      role="region"
      aria-label="AI edits review table"
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        border: '1px solid',
        borderColor: THEME_COLORS.border.default,
      }}
    >
      <Table aria-label="Sentences with AI edits for review">
        <TableHead>
          <TableRow
            sx={{
              bgcolor: '#F7FAFC',
            }}
          >
            <TableCell
              variant="body1"
              sx={{
                fontWeight: 600,
                color: THEME_COLORS.text.primary,
                borderBottom: '2px solid',
                borderColor: THEME_COLORS.border.default,
                textTransform: 'uppercase',
              }}
            >
              Original vs Edited Text
            </TableCell>

            <TableCell
              width="120px"
              sx={{
                fontWeight: 600,
                color: THEME_COLORS.text.primary,
                borderBottom: '2px solid',
                borderColor: THEME_COLORS.border.default,
              }}
            >
              <TableSortLabel
                active={sortBy === SORT_FIELDS.SIMILARITY}
                direction={
                  sortBy === SORT_FIELDS.SIMILARITY
                    ? sortOrder === 'asc'
                      ? 'asc'
                      : 'desc'
                    : 'asc'
                }
                onClick={() => onSort(SORT_FIELDS.SIMILARITY)}
                aria-label="Sort by similarity score"
                sx={{
                  textTransform: 'uppercase',
                  '&.Mui-active': {
                    color: THEME_COLORS.primary,
                    '& .MuiTableSortLabel-icon': {
                      color: `${THEME_COLORS.primary} !important`,
                    },
                  },
                }}
              >
                Similarity
              </TableSortLabel>
            </TableCell>

            <TableCell
              width="140px"
              sx={{
                fontWeight: 600,
                color: THEME_COLORS.text.primary,
                borderBottom: '2px solid',
                borderColor: THEME_COLORS.border.default,
              }}
            >
              <TableSortLabel
                active={sortBy === SORT_FIELDS.SECTION}
                direction={
                  sortBy === SORT_FIELDS.SECTION
                    ? sortOrder === 'asc'
                      ? 'asc'
                      : 'desc'
                    : 'asc'
                }
                onClick={() => onSort(SORT_FIELDS.SECTION)}
                aria-label="Sort by section"
                sx={{
                  textTransform: 'uppercase',
                  '&.Mui-active': {
                    color: THEME_COLORS.primary,
                    '& .MuiTableSortLabel-icon': {
                      color: `${THEME_COLORS.primary} !important`,
                    },
                  },
                }}
              >
                Section
              </TableSortLabel>
            </TableCell>

            <TableCell
              width="160px"
              sx={{
                fontWeight: 600,
                color: THEME_COLORS.text.primary,
                borderBottom: '2px solid',
                borderColor: THEME_COLORS.border.default,
                textTransform: 'uppercase',
              }}
            >
              Actions
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {filteredAndSortedSentences.map((sentence, index) => {
            const isProcessing = processingIds.has(sentence.tuid);
            const isExpanded = expandedRows.has(sentence.tuid);
            const editType = getEditType(sentence);

            return (
              <React.Fragment key={sentence.tuid}>
                <TableRow
                  hover
                  sx={{
                    bgcolor:
                      index % 2 === 0
                        ? THEME_COLORS.background.page
                        : THEME_COLORS.background.card,
                    '&:hover': {
                      bgcolor: THEME_COLORS.background.hover,
                    },
                    transition: 'background-color 0.2s',
                  }}
                >
                  <TableCell
                    sx={{
                      py: 2.5,
                      borderBottom: isExpanded ? 'none' : undefined,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Tooltip
                        title={
                          <Typography sx={{ color: '#FFF', fontSize: '14px' }}>
                            {isExpanded ? 'Hide details' : 'Show details'}
                          </Typography>
                        }
                      >
                        <IconButton
                          size="small"
                          onClick={() => onToggleExpand(sentence.tuid)}
                          aria-label={
                            isExpanded ? 'Hide sentence details' : 'Show sentence details'
                          }
                          aria-expanded={isExpanded}
                          sx={{
                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            mt: 0.5,
                            color: THEME_COLORS.text.tertiary,
                            '&:hover': {
                              bgcolor: alpha(THEME_COLORS.primary, 0.1),
                              color: THEME_COLORS.primary,
                            },
                          }}
                        >
                          <ExpandMoreIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Box sx={{ maxWidth: 600, flex: 1 }}>
                        {/* Edit Type Badge */}
                        <Box mb={1}>
                          <Chip
                            label={editType.charAt(0).toUpperCase() + editType.slice(1)}
                            sx={{
                              fontWeight: 500,
                              bgcolor: alpha(THEME_COLORS.primary, 0.1),
                              color: THEME_COLORS.primary,
                              border: 'none',
                            }}
                          />
                        </Box>

                        {sentence.edited_value || sentence.edited_text ? (
                          <TextDiff
                            original={(sentence.value || sentence.original_text || '').replace(/\n+/g, ' ').replace(/\s{2,}/g, ' ').trim()}
                            modified={(sentence.edited_value || sentence.edited_text || '').replace(/\n+/g, ' ').replace(/\s{2,}/g, ' ').trim()}
                          />
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{
                              color: THEME_COLORS.text.primary,
                              lineHeight: 1.6,
                            }}
                          >
                            {sentence.value ||
                              sentence.original_text ||
                              'No text available'}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>

                  <TableCell
                    sx={{
                      py: 2.5,
                      borderBottom: isExpanded ? 'none' : undefined,
                    }}
                  >
                    <Box
                      display="flex"
                      flexDirection="column"
                      gap={0.5}
                      alignItems="center"
                    >
                      <Chip
                        label={`${Math.round((sentence.score || 0) * 100)}%`}
                        sx={{
                          fontWeight: 600,
                          bgcolor:
                            (sentence.score || 0) >= SIMILARITY_THRESHOLDS.HIGH
                              ? alpha(THEME_COLORS.success, 0.15)
                              : (sentence.score || 0) >= SIMILARITY_THRESHOLDS.MEDIUM
                                ? alpha(THEME_COLORS.warning, 0.15)
                                : alpha(THEME_COLORS.error, 0.15),
                          color:
                            (sentence.score || 0) >= SIMILARITY_THRESHOLDS.HIGH
                              ? THEME_COLORS.success
                              : (sentence.score || 0) >= SIMILARITY_THRESHOLDS.MEDIUM
                                ? THEME_COLORS.warning
                                : THEME_COLORS.error,
                          border: 'none',
                        }}
                      />

                      <Typography
                        variant="body2"
                        sx={{
                          color: THEME_COLORS.text.primary,
                          lineHeight: 1.6,
                          textAlign: 'center',
                        }}
                      >
                        {getScoreLabel(sentence.score || 0)}
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell
                    sx={{
                      py: 2.5,
                      borderBottom: isExpanded ? 'none' : undefined,
                    }}
                  >
                    <Typography
                      noWrap
                      variant="body2"
                      sx={{
                        color: THEME_COLORS.text.primary,
                        lineHeight: 1.6,
                      }}
                    >
                      {getSectionNames(sentence).join(', ')}
                    </Typography>
                  </TableCell>

                  <TableCell
                    sx={{
                      py: 2.5,
                      borderBottom: isExpanded ? 'none' : undefined,
                    }}
                  >
                    {sentence.status === STATUS_TYPES.APPROVED ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                          label="Approved"
                          size="small"
                          icon={<CheckIcon sx={{ fontSize: 16 }} />}
                          sx={{
                            bgcolor: alpha(THEME_COLORS.success, 0.15),
                            color: THEME_COLORS.success,
                            fontWeight: 600,
                            border: `1px solid ${alpha(THEME_COLORS.success, 0.3)}`,
                          }}
                        />
                        <Tooltip
                          title={
                            <Typography sx={{ color: '#FFF', fontSize: '14px' }}>
                              Undo approval
                            </Typography>
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={() => onUndo(sentence)}
                            disabled={isProcessing}
                            aria-label="Undo approval"
                            aria-busy={isProcessing}
                            sx={{
                              color: THEME_COLORS.text.tertiary,
                              '&:hover': {
                                bgcolor: alpha(THEME_COLORS.primary, 0.1),
                                color: THEME_COLORS.primary,
                              },
                            }}
                          >
                            {isProcessing ? (
                              <CircularProgress size={16} thickness={4} />
                            ) : (
                              <UndoIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : sentence.status === STATUS_TYPES.DISAPPROVED ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                          label="Rejected"
                          size="small"
                          icon={<CloseIcon sx={{ fontSize: 16 }} />}
                          sx={{
                            bgcolor: alpha(THEME_COLORS.error, 0.15),
                            color: THEME_COLORS.error,
                            fontWeight: 600,
                            border: `1px solid ${alpha(THEME_COLORS.error, 0.3)}`,
                          }}
                        />
                        <Tooltip
                          title={
                            <Typography sx={{ color: '#FFF', fontSize: '14px' }}>
                              Undo rejection
                            </Typography>
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={() => onUndo(sentence)}
                            disabled={isProcessing}
                            aria-label="Undo rejection"
                            aria-busy={isProcessing}
                            sx={{
                              color: THEME_COLORS.text.tertiary,
                              '&:hover': {
                                bgcolor: alpha(THEME_COLORS.primary, 0.1),
                                color: THEME_COLORS.primary,
                              },
                            }}
                          >
                            {isProcessing ? (
                              <CircularProgress size={16} thickness={4} />
                            ) : (
                              <UndoIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      <Box display="flex" gap={1}>
                        <Tooltip
                          title={
                            <Typography sx={{ color: '#FFF', fontSize: '14px' }}>
                              Approve sentence
                            </Typography>
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={() => onApprove(sentence)}
                            disabled={isProcessing}
                            aria-label="Approve sentence"
                            aria-busy={isProcessing}
                            sx={{
                              color: THEME_COLORS.success,
                              bgcolor: alpha(THEME_COLORS.success, 0.1),
                              '&:hover': {
                                bgcolor: alpha(THEME_COLORS.success, 0.2),
                                transform: 'scale(1.05)',
                              },
                              '&:disabled': {
                                bgcolor: alpha('#CBD5E0', 0.5),
                              },
                              transition: 'all 0.2s',
                            }}
                          >
                            {isProcessing ? (
                              <CircularProgress size={16} thickness={4} />
                            ) : (
                              <CheckIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          title={
                            <Typography sx={{ color: '#FFF', fontSize: '14px' }}>
                              Reject sentence
                            </Typography>
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={() => onDisapprove(sentence)}
                            disabled={isProcessing}
                            aria-label="Reject sentence"
                            aria-busy={isProcessing}
                            sx={{
                              color: THEME_COLORS.error,
                              bgcolor: alpha(THEME_COLORS.error, 0.1),
                              '&:hover': {
                                bgcolor: alpha(THEME_COLORS.error, 0.2),
                                transform: 'scale(1.05)',
                              },
                              '&:disabled': {
                                bgcolor: alpha('#CBD5E0', 0.5),
                              },
                              transition: 'all 0.2s',
                            }}
                          >
                            {isProcessing ? (
                              <CircularProgress size={16} thickness={4} />
                            ) : (
                              <CloseIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
                {/* Collapsible info row */}
                <TableRow>
                  <TableCell
                    colSpan={4}
                    sx={{
                      paddingTop: 0,
                      paddingBottom: 0,
                      borderBottom: isExpanded ? undefined : 'none',
                      bgcolor:
                        index % 2 === 0
                          ? THEME_COLORS.background.page
                          : THEME_COLORS.background.card,
                    }}
                  >
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Box
                        sx={{
                          py: 2.5,
                          px: 3,
                          my: 1.5,
                          bgcolor: alpha(THEME_COLORS.background.card, 0.5),
                          borderRadius: 1.5,
                          border: '1px solid',
                          borderColor: THEME_COLORS.border.default,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: THEME_COLORS.text.primary,
                            textTransform: 'uppercase',
                            lineHeight: 1.6,
                            fontWeight: 600,
                            mb: 1.5,
                          }}
                        >
                          Seed Information
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {sentence.sourceText && (
                            <Box>
                              <Typography
                                variant="body1"
                                sx={{
                                  color: THEME_COLORS.text.primary,
                                  fontWeight: 600,
                                }}
                              >
                                Seed Sentence
                              </Typography>

                              <Typography
                                variant="body2"
                                sx={{
                                  mt: 0.75,
                                  color: THEME_COLORS.text.primary,
                                  lineHeight: 1.6,
                                  p: 1.5,
                                  bgcolor: THEME_COLORS.background.page,
                                  borderRadius: 1,
                                  border: '1px solid',
                                  borderColor: THEME_COLORS.border.default,
                                }}
                              >
                                {sentence.sourceText}
                              </Typography>
                            </Box>
                          )}

                          {sentence.sourceTuid && (
                            <Box>
                              <Typography
                                variant="body1"
                                sx={{
                                  color: THEME_COLORS.text.primary,
                                  fontWeight: 600,
                                }}
                              >
                                Training ID
                              </Typography>

                              <Typography
                                variant="body2"
                                sx={{
                                  mt: 0.75,
                                  color: THEME_COLORS.text.primary,
                                  lineHeight: 1.6,
                                  p: 1.5,
                                  bgcolor: THEME_COLORS.background.page,
                                  borderRadius: 1,
                                  border: '1px solid',
                                  borderColor: THEME_COLORS.border.default,
                                }}
                              >
                                {sentence.sourceTuid}
                              </Typography>
                            </Box>
                          )}
                          {!sentence.sourceText && !sentence.sourceTuid && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: THEME_COLORS.text.primary,
                                textTransform: 'uppercase',
                                lineHeight: 1.6,
                                fontWeight: 600,
                              }}
                            >
                              No seed information available
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

AIEditsTabTable.propTypes = {
  filteredAndSortedSentences: PropTypes.arrayOf(
    PropTypes.shape({
      tuid: PropTypes.string.isRequired,
      value: PropTypes.string,
      original_text: PropTypes.string,
      edited_value: PropTypes.string,
      edited_text: PropTypes.string,
      score: PropTypes.number,
      status: PropTypes.string,
      sections: PropTypes.oneOfType([
        PropTypes.arrayOf(
          PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({
              name: PropTypes.string,
              id: PropTypes.string,
            }),
          ])
        ),
        PropTypes.string,
      ]),
      section: PropTypes.string,
    })
  ).isRequired,
  sortBy: PropTypes.oneOf(Object.values(SORT_FIELDS)).isRequired,
  sortOrder: PropTypes.oneOf(['asc', 'desc']).isRequired,
  expandedRows: PropTypes.instanceOf(Set).isRequired,
  processingIds: PropTypes.instanceOf(Set).isRequired,
  onSort: PropTypes.func.isRequired,
  onToggleExpand: PropTypes.func.isRequired,
  onApprove: PropTypes.func.isRequired,
  onDisapprove: PropTypes.func.isRequired,
  onUndo: PropTypes.func.isRequired,
};

export default React.memo(AIEditsTabTable, (prevProps, nextProps) => {
  // Deep comparison for Sets (can't use reference equality)
  const expandedRowsEqual =
    prevProps.expandedRows.size === nextProps.expandedRows.size &&
    [...prevProps.expandedRows].every((id) => nextProps.expandedRows.has(id));

  const processingIdsEqual =
    prevProps.processingIds.size === nextProps.processingIds.size &&
    [...prevProps.processingIds].every((id) => nextProps.processingIds.has(id));

  // Compare arrays by reference (parent should memoize filtered/sorted arrays)
  const sentencesEqual =
    prevProps.filteredAndSortedSentences === nextProps.filteredAndSortedSentences;

  // Compare other props
  const otherPropsEqual =
    prevProps.sortBy === nextProps.sortBy &&
    prevProps.sortOrder === nextProps.sortOrder &&
    prevProps.onSort === nextProps.onSort &&
    prevProps.onToggleExpand === nextProps.onToggleExpand &&
    prevProps.onApprove === nextProps.onApprove &&
    prevProps.onDisapprove === nextProps.onDisapprove &&
    prevProps.onUndo === nextProps.onUndo;

  return expandedRowsEqual && processingIdsEqual && sentencesEqual && otherPropsEqual;
});
